import { c as _c } from "react/compiler-runtime";
import ThemedAvatar from 'shared/components/Themed/Avatar';
import Grid from '@precis-digital/kurama/Grid';
import { styled } from '@precis-digital/kurama/utils';
import { uniq } from 'ramda';
import { useMemo, type ReactElement } from 'react';
import { StyledTypography1LineClamp } from 'shared/components/StyledTypography1LineClamp';
import { useTranslation } from 'shared/translations';
import { pluralToSingle } from 'shared/utils/stringFormatter';
import { type GroupedRecommendationItem } from './types';
interface UseEntitiesAffectedReturn {
  entities: string[] | null;
  entitiesLabel: string;
  entitiesTooltipTitle: string | null;
  entitiesTooltipBody: ReactElement | null;
}
export const useEntitiesAffected = (t0, t1) => {
  const $ = _c(24);
  const {
    defaultColumns,
    platformEntityType,
    outputColumns,
    events,
    totalNumEntitiesWithIssues,
    totalNumEntities
  } = t0;
  const useOutOf = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation("recommendation");
  let t2;
  if ($[0] !== platformEntityType) {
    t2 = () => platformEntityType.replace(/_/g, " ");
    $[0] = platformEntityType;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getEntitiesLabel = t2;
  let t3;
  if ($[2] !== getEntitiesLabel || $[3] !== t || $[4] !== totalNumEntities || $[5] !== totalNumEntitiesWithIssues || $[6] !== useOutOf) {
    const getEntitiesAffectedDescriber = () => {
      const entitiesLabel = pluralToSingle(getEntitiesLabel(), totalNumEntitiesWithIssues);
      if (totalNumEntities === 0 || !useOutOf) {
        return entitiesLabel;
      }
      if (totalNumEntitiesWithIssues < totalNumEntities) {
        return `${entitiesLabel} (${t("affectedOutOf")} ${totalNumEntities})`;
      }
      return `${entitiesLabel} (${t("affectedAll")})`;
    };
    t3 = getEntitiesAffectedDescriber();
    $[2] = getEntitiesLabel;
    $[3] = t;
    $[4] = totalNumEntities;
    $[5] = totalNumEntitiesWithIssues;
    $[6] = useOutOf;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const entitiesLabel_0 = t3;
  const columnIdEntityName = defaultColumns[0];
  let t4;
  if ($[8] !== columnIdEntityName || $[9] !== events || $[10] !== outputColumns) {
    bb0: {
      if (columnIdEntityName == null) {
        let t5;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = {
            entities: null,
            entitiesTooltipTitle: null,
            entitiesTooltipBody: null
          };
          $[12] = t5;
        } else {
          t5 = $[12];
        }
        t4 = t5;
        break bb0;
      }
      let t5;
      if ($[13] !== columnIdEntityName) {
        t5 = t6 => {
          const {
            data
          } = t6;
          return data.map(item => String(item[columnIdEntityName]));
        };
        $[13] = columnIdEntityName;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      const entities = uniq(events.flatMap(t5));
      if (entities.length === 0) {
        t4 = {
          entities,
          entitiesTooltipTitle: null,
          entitiesTooltipBody: null
        };
        break bb0;
      }
      const entitiesTooltipTitle = outputColumns?.[columnIdEntityName]?.label ?? columnIdEntityName;
      const entitiesTooltipBody = <Grid display="flex" flexDirection="column" gap="4px">{entities.map(_temp)}</Grid>;
      let t6;
      if ($[15] !== entities || $[16] !== entitiesTooltipBody || $[17] !== entitiesTooltipTitle) {
        t6 = {
          entities,
          entitiesTooltipTitle,
          entitiesTooltipBody
        };
        $[15] = entities;
        $[16] = entitiesTooltipBody;
        $[17] = entitiesTooltipTitle;
        $[18] = t6;
      } else {
        t6 = $[18];
      }
      t4 = t6;
    }
    $[8] = columnIdEntityName;
    $[9] = events;
    $[10] = outputColumns;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    entities: entities_0,
    entitiesTooltipBody: entitiesTooltipBody_0,
    entitiesTooltipTitle: entitiesTooltipTitle_0
  } = t4;
  let t5;
  if ($[19] !== entitiesLabel_0 || $[20] !== entitiesTooltipBody_0 || $[21] !== entitiesTooltipTitle_0 || $[22] !== entities_0) {
    t5 = {
      entities: entities_0,
      entitiesLabel: entitiesLabel_0,
      entitiesTooltipTitle: entitiesTooltipTitle_0,
      entitiesTooltipBody: entitiesTooltipBody_0
    };
    $[19] = entitiesLabel_0;
    $[20] = entitiesTooltipBody_0;
    $[21] = entitiesTooltipTitle_0;
    $[22] = entities_0;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
};
interface UseAccountsAffectedReturn {
  avatars: Array<{
    name: string;
    iconUrl: string;
  }> | null;
  avatarLabel: string;
  avatarsTooltipTitle: string | null;
  avatarsTooltipBody: ReactElement | null;
}
export const useAccountsAvatarsAffected: (recommendationItem: GroupedRecommendationItem) => UseAccountsAffectedReturn = t0 => {
  const $ = _c(29);
  const {
    accounts
  } = t0;
  const {
    t
  } = useTranslation("recommendation");
  let t1;
  if ($[0] !== accounts.length || $[1] !== t) {
    t1 = accounts.length === 1 ? t("account") : t("accounts");
    $[0] = accounts.length;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const avatarLabel = t1;
  let t2;
  bb0: {
    if (accounts.length === 0) {
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          avatars: null,
          avatarsTooltipTitle: null,
          avatarsTooltipBody: null
        };
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      t2 = t3;
      break bb0;
    }
    let T0;
    let avatars;
    let t3;
    let t4;
    let t5;
    let t6;
    if ($[4] !== accounts) {
      avatars = accounts.slice().sort(_temp2);
      T0 = Grid;
      t3 = "flex";
      t4 = "column";
      t5 = "4px";
      t6 = avatars.map(_temp3);
      $[4] = accounts;
      $[5] = T0;
      $[6] = avatars;
      $[7] = t3;
      $[8] = t4;
      $[9] = t5;
      $[10] = t6;
    } else {
      T0 = $[5];
      avatars = $[6];
      t3 = $[7];
      t4 = $[8];
      t5 = $[9];
      t6 = $[10];
    }
    let t7;
    if ($[11] !== T0 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
      t7 = <T0 display={t3} flexDirection={t4} gap={t5}>{t6}</T0>;
      $[11] = T0;
      $[12] = t3;
      $[13] = t4;
      $[14] = t5;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const avatarsTooltipBody = t7;
    let t8;
    if ($[17] !== avatars.length || $[18] !== t) {
      t8 = avatars.length === 1 ? t("affectedAccount") : t("affectedAccounts");
      $[17] = avatars.length;
      $[18] = t;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    const avatarsTooltipTitle = t8;
    let t9;
    if ($[20] !== avatars || $[21] !== avatarsTooltipBody || $[22] !== avatarsTooltipTitle) {
      t9 = {
        avatars,
        avatarsTooltipTitle,
        avatarsTooltipBody
      };
      $[20] = avatars;
      $[21] = avatarsTooltipBody;
      $[22] = avatarsTooltipTitle;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t2 = t9;
  }
  const {
    avatars: avatars_0,
    avatarsTooltipBody: avatarsTooltipBody_0,
    avatarsTooltipTitle: avatarsTooltipTitle_0
  } = t2;
  let t3;
  if ($[24] !== avatarLabel || $[25] !== avatarsTooltipBody_0 || $[26] !== avatarsTooltipTitle_0 || $[27] !== avatars_0) {
    t3 = {
      avatars: avatars_0,
      avatarLabel,
      avatarsTooltipTitle: avatarsTooltipTitle_0,
      avatarsTooltipBody: avatarsTooltipBody_0
    };
    $[24] = avatarLabel;
    $[25] = avatarsTooltipBody_0;
    $[26] = avatarsTooltipTitle_0;
    $[27] = avatars_0;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  return t3;
};
const StyledTypographyAccountId = styled(StyledTypography1LineClamp)(() => ({
  marginTop: '-2px'
}));
function _temp(entity) {
  return <StyledTypography1LineClamp variant="body2" key={entity}>{entity}</StyledTypography1LineClamp>;
}
function _temp2(a, b) {
  return a.name.localeCompare(b.name);
}
function _temp3(account) {
  return <Grid key={account.id} display="flex" gap="8px" alignItems="center"><ThemedAvatar size="small" kind="image" imageUrl={account.iconUrl} /><Grid display="flex" flexDirection="column"><StyledTypography1LineClamp variant="body2">{account.name}</StyledTypography1LineClamp><StyledTypographyAccountId variant="body3">{account.id}</StyledTypographyAccountId></Grid></Grid>;
}